/* Copyright (c) 2017 Backpack Health LLC */
/* global window */
import React from 'react';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';

import { SecondaryButton, CloseButton } from '../buttons/Buttons';
import FlexRow from '../FlexRow';

import Spacer from '../Spacer';
import IconWarningIcons from '../icons/IconWarning';

import { actions as systemModalActions } from '../../redux/ducks/systemModal';
import PreformattedText from '../PreformattedText';
import { ExternalOrInBrowserlLink } from '../TextLink';
import AppUtils from '../../../utilities/AppUtils';
import { versionCheck } from '../../../utilities/LocalStorage';
import { TrackEvent } from '../../../utilities/UserMetrics';
import { openInAppBrowser, goToSettings } from '../../../utilities/MobileUtil';

function mapStateToProps() {
  return {};
}

function mapDispatchToProps(dispatch) {
  return {
    dismiss: () => {
      dispatch(systemModalActions.dismiss());
    },
  };
}

interface PhraseType {
  id: string;
  values?: { [key: string]: any; };
}

interface Props {
  intl: any;
  dismiss(): void;
  phraseMessages: {
    title: PhraseType;
    subheading: PhraseType;
    body: PhraseType | PhraseType[];
    button: PhraseType;
    alternateButton?: PhraseType;
  };
  onDismiss?(): void;
  onConfirm(): void;
  onAlternate?(): void;
  hasCloseButton: boolean;
  titleClassName: string;
  iconColorName: string;
  buttonClassName: string;
  isBodyPreformatted: boolean;
  onView?(): void;
}

class WarningModal extends React.Component<Props, {}> {

  componentDidMount() {
    if (this.props.onView) {
      this.props.onView();
    }
  }

  onDismiss = () => {
    this.props.dismiss();

    if (this.props.onDismiss) { this.props.onDismiss(); }
  }

  onConfirm = () => {
    this.props.dismiss();

    if (this.props.onConfirm) {
      this.props.onConfirm();
    }
  }

  onAlternate = async () => {
    await this.props.onAlternate();
    this.props.dismiss();
  }

  render() {
    const { isBodyPreformatted, phraseMessages, hasCloseButton, titleClassName, iconColorName, buttonClassName, onAlternate } = this.props;
    const bodyPhrases = (Array.isArray(phraseMessages.body) ? phraseMessages.body : [phraseMessages.body]).filter(p => !!p);

    return (
      <div className="cmp-warningmodal">
        <FlexRow style={{ alignItems: 'flex-start' }}>
          <div><IconWarningIcons.XLarge strokeClassName={iconColorName} /></div>
          {
            hasCloseButton && <CloseButton align="right" verticalAlign="top" className="bkgd-color-gsMiddleDT" onClick={this.onDismiss} />
          }
        </FlexRow>

        <Spacer size="12" />

        <div className={`font-title-2 ${titleClassName}`}>
          <FormattedMessage {...phraseMessages.title} />
        </div>

        {
          phraseMessages.subheading ? (
            <>
              <Spacer size="4" />

              <div className="font-headline font-color-gsDark">
                <FormattedMessage {...phraseMessages.subheading} />
              </div>
            </>
          ) : null
        }

        <Spacer size="12" />

        {
          isBodyPreformatted ? (
              <div className="__body">
                {
                  bodyPhrases.map((p, i) => (
                    <PreformattedText key={i} className="font-subhead font-color-gsDark">
                      <FormattedMessage {...p} />
                    </PreformattedText>
                  ))
                }
                {
                  this.props.children ? (
                    <div style={{ marginTop: 16 }}>
                      {this.props.children}
                    </div>
                  ) : null
                }
              </div>
          ) : (
            <div className="__body">
              {
                bodyPhrases.map((p, i) => (
                  <PreformattedText key={i} className="font-subhead font-color-gsDark">
                    <FormattedMessage {...p} />
                  </PreformattedText>
                ))
              }
              {
                this.props.children ? (
                  <div style={{ marginTop: 16 }}>
                    {this.props.children}
                  </div>
                ) : null
              }
            </div>
          )
        }

        <Spacer size="16" />

        <div className="control">
          <SecondaryButton className={`font-title-2 ${buttonClassName}`} onClick={this.onConfirm} style={{ wordBreak: 'break-word' }}>
            <FormattedMessage {...phraseMessages.button} />
          </SecondaryButton>
        </div>

        {
          phraseMessages.alternateButton && (
            <div className="cmp-alternate-control font-subhead font-color-gsDark" onClick={this.onAlternate}>
              <FormattedMessage {...phraseMessages.alternateButton} />
            </div>
          )
        }
      </div>
    );
  }
}

export const ProfileThemeedWarningModal = connect(mapStateToProps, mapDispatchToProps)((props) => (
  <WarningModal
    {...props}
    titleClassName="font-color-profile"
    iconColorName="stroke-color-profile-accent"
    buttonClassName="font-color-profile-accent border-profile-accent"
  />
));

export const GroupThemeedWarningModal = connect(mapStateToProps, mapDispatchToProps)((props) => (
  <WarningModal
    {...props}
    titleClassName="theme-group base color"
    iconColorName="theme-group accent stroke"
    buttonClassName="theme-group accent color border"
  />
));

export const BreakingChangeWarningModal = (props) => {
  const latestVersion = props.changes[0].version;

  return (
    <ProfileThemeedWarningModal
      hasCloseButton={true}
      onView={() => {
        TrackEvent.backpackProfile.updateModalViewed();
      }}
      phraseMessages={{
        title: {
          id: 'release.updateApp.heading',
        },
        subheading: null,
        body: {
          id: 'release.updateApp.message',
        },
        button: {
          id: 'release.updateApp.update.button.label',
        },
        alternateButton: {
          id: 'release.updateApp.dismiss.button.label',
        },
      }}
      onConfirm={() => {
        TrackEvent.backpackProfile.updateModalUpdateButton();
        openInAppBrowser(AppUtils.googlePlayAppLink());
      }}
      onAlternate={() => {
        TrackEvent.backpackProfile.updateModalDismissButton();
        versionCheck.set(latestVersion);
      }}
    >
      <FormattedMessage id="app.releaseNotes.url">
        {
          (url: string) => (
            <ExternalOrInBrowserlLink url={url} className="font-color-profile-accent">
              <FormattedMessage id="app.releaseNotes.button.label" />
            </ExternalOrInBrowserlLink>
          )
        }
      </FormattedMessage>

    </ProfileThemeedWarningModal>
  );
};

export enum requiredPermissionTypes {
  image = 'image',
  video = 'video',
  library = 'library',
  notifications = 'notifications',
}
const permissionMessages = {
  [requiredPermissionTypes.image]: 'PermissionsRequired.AndroidCamera.message',
  [requiredPermissionTypes.video]: 'PermissionsRequired.AndroidCamera.message',
  [requiredPermissionTypes.library]: 'PermissionsRequired.device.message',
  [requiredPermissionTypes.notifications]: 'permissionsRequired.notifications.message',
};

interface PermissionsRequiredModalProps {
  permissions: requiredPermissionTypes[];
  onDismiss?(): void;
}
export const PermissionsRequiredModal = (props: PermissionsRequiredModalProps) => {
  return (
    <ProfileThemeedWarningModal
      hasCloseButton={true}
      phraseMessages={{
        title: {
          id: 'PermissionsRequired.heading',
        },
        subheading: null,
        body: props.permissions.map(p => ({ id: permissionMessages[p] })),
        button: {
          id: 'PermissionsRequired.settings.button.label',
        },
      }}
      onConfirm={() => {
        goToSettings('application_details');
      }}
      onDismiss={props.onDismiss}
    />
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(WarningModal);
